import { getByID } from '@adornis/baseql/operations/mongo.js';
import { countingTableView } from '@nsc/simple-lms/client/record-count-view.js';
import type { ERMEntityDefinition } from '@adornis/entity-relationship-management/client/types.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import {
  countScopedAvailabilities,
  getFieldEngineerById,
  getScopedFieldEngineers,
  subscribeScopedAvailabilitiesPaginated,
} from '../../api/scoped-queries.js';
import { softDeleteAvailability } from '../../api/soft-delete.js';
import { SmartTeamsAvailability, SmartTeamsFieldEngineer, SmartTeamsLocation } from '../../db/index.js';
import { FieldEngineerName } from '../components/field-engineer-name.js';
import { formatClockTime, formatDate } from '../date-format.js';
import { showCreateAvailabilityDialog } from '../dialogs/index.js';
import { confirmAndSoftDelete } from './soft-delete.js';

// Location section that shows either the FE's default location or input fields
const LocationSection = stateful(({ availability }: { availability: SmartTeamsAvailability }) => {
  // Load the field engineer to get their default location
  const feQuery = useQuery(
    () =>
      availability.fieldEngineerId
        ? getFieldEngineerById(availability.fieldEngineerId)({
            _id: 1,
            name: 1,
            defaultLocation: { street: 1, number: 1, city: 1, zipCode: 1, country: 1 },
          })
        : Promise.resolve(null),
    [availability.fieldEngineerId],
  );

  const fe = feQuery.value as SmartTeamsFieldEngineer | null;
  const defaultLoc = fe?.defaultLocation;

  // Helper to ensure location exists and update a field
  const updateLocationField = (field: keyof SmartTeamsLocation, value: string | null) => {
    if (!availability.location) availability.location = new SmartTeamsLocation({});
    (availability.location as any)[field] = value || undefined;
  };

  // If using default location, show the FE's address
  if (availability.useDefaultLocation) {
    if (feQuery.loading) {
      return html`<span class="loading loading-spinner loading-sm"></span>`;
    }

    if (!defaultLoc || (!defaultLoc.city && !defaultLoc.street)) {
      return html`
        <div class="flex items-center gap-2 text-sm text-warning py-2">
          <span class="icon-[mdi--map-marker-question] text-lg"></span>
          <span>No default location set for the field engineer.</span>
        </div>
      `;
    }

    const addressParts = [
      defaultLoc.street && defaultLoc.number ? `${defaultLoc.street} ${defaultLoc.number}` : defaultLoc.street,
      [defaultLoc.zipCode, defaultLoc.city].filter(Boolean).join(' '),
      defaultLoc.country,
    ].filter(Boolean);

    return html`
      <div class="flex items-start gap-3 py-2">
        <span class="icon-[mdi--map-marker-check] text-xl text-success mt-0.5"></span>
        <div class="flex flex-col">
          <span class="text-sm font-medium">Default location of ${fe?.name ?? 'field engineer'}</span>
          <span class="text-sm text-base-content/70">${addressParts.join(', ')}</span>
        </div>
      </div>
    `;
  }

  // If not using default location, show input fields
  return html`
    <p class="text-sm text-base-content/60 mb-4">Provide a different location for this availability.</p>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="form-control">
        <label class="label"><span class="label-text">Street</span></label>
        ${TextInput({
          value: availability.location?.street ?? '',
          placeholder: 'e.g. Example Street',
          onPicked: value => updateLocationField('street', value),
        })}
      </div>
      <div class="form-control">
        <label class="label"><span class="label-text">Number</span></label>
        ${TextInput({
          value: availability.location?.number ?? '',
          placeholder: 'e.g. 42',
          onPicked: value => updateLocationField('number', value),
        })}
      </div>
      <div class="form-control">
        <label class="label"><span class="label-text">City</span></label>
        ${TextInput({
          value: availability.location?.city ?? '',
          placeholder: 'e.g. Berlin',
          onPicked: value => updateLocationField('city', value),
        })}
      </div>
      <div class="form-control">
        <label class="label"><span class="label-text">Postal code</span></label>
        ${TextInput({
          value: availability.location?.zipCode ?? '',
          placeholder: 'e.g. 10115',
          onPicked: value => updateLocationField('zipCode', value),
        })}
      </div>
      <div class="form-control md:col-span-2">
        <label class="label"><span class="label-text">Country</span></label>
        ${TextInput({
          value: availability.location?.country ?? '',
          placeholder: 'e.g. Germany',
          onPicked: value => updateLocationField('country', value),
        })}
      </div>
    </div>
  `;
});

// Field Engineer picker component for availability detail view
const FieldEngineerPicker = stateful(
  ({
    value,
    onChange,
    label,
  }: {
    value: string | undefined;
    onChange: (feId: string | undefined) => void;
    label?: string;
  }) => {
    const fesQuery = useQuery(() => getScopedFieldEngineers()({ _id: 1, name: 1, email: 1 }), []);
    const fieldEngineers = (fesQuery.value ?? []) as SmartTeamsFieldEngineer[];
    const selectedFE = fieldEngineers.find(fe => fe._id === value);

    if (fesQuery.loading) {
      return html`<span class="loading loading-spinner loading-sm"></span>`;
    }

    return html`
      <div class="form-control w-full">
        ${label ? html`<label class="label"><span class="label-text">${label}</span></label>` : ''}
        ${DropdownSelectionInput<SmartTeamsFieldEngineer, false>({
          multi: false,
          placeholder: 'Pick field engineer...',
          selectables: fieldEngineers,
          value: selectedFE,
          clearable: true,
          renderString: fe => `${fe.name}${fe.email ? ` (${fe.email})` : ''}`,
          resolveID: fe => fe._id,
          emptyState: 'No field engineers available',
          onPicked: value => onChange(value?._id),
        })}
      </div>
    `;
  },
);

export const availabilityManagerConfig: ERMEntityDefinition<typeof SmartTeamsAvailability> = {
  _class: SmartTeamsAvailability,
  label: 'Availabilities',
  icon: 'icon-[mdi--calendar-clock-outline]',
  route: 'availabilities',
  detail: {
    operation: id => getByID(SmartTeamsAvailability, id),
    selectionSet: {
      _id: 1,
      fieldEngineerId: 1,
      date: 1,
      startTime: 1,
      endTime: 1,
      useDefaultLocation: 1,
      notes: 1,
      createdBy: 1,
      createdAt: 1,
      location: {
        street: 1,
        number: 1,
        city: 1,
        zipCode: 1,
        country: 1,
      },
    },

    title: e => {
      const dateStr = e.date ? formatDate(e.date) : 'New availability';
      return `${dateStr} ${formatClockTime(e.startTime)} - ${formatClockTime(e.endTime)}`;
    },
    sections: [
      {
        title: 'Availability details',
        fields: [
          {
            fieldName: 'fieldEngineerId',
            render: ({ entity }) =>
              FieldEngineerPicker({
                label: 'Field Engineer',
                value: entity?.fieldEngineerId ?? undefined,
                onChange: feId => {
                  if (entity) entity.fieldEngineerId = feId;
                },
              }),
          },
          { fieldName: 'date', label: 'Date' },
          { fieldName: 'startTime', label: 'Start time' },
          { fieldName: 'endTime', label: 'End time' },
          { fieldName: 'useDefaultLocation', label: 'Default location' },
        ],
      },
      {
        title: 'Location',
        render: ({ entity }) => (entity ? LocationSection({ availability: entity }) : nothing),
      },
      {
        title: 'Notes',
        fields: [{ fieldName: 'notes', label: 'Notes' }],
      },
    ],
    onDelete: entity =>
      confirmAndSoftDelete({
        entityLabel: 'availability',
        name: entity.date
          ? `${new Date(entity.date).toLocaleDateString('de-DE')} ${entity.startTime ?? ''} - ${entity.endTime ?? ''}`.trim()
          : null,
        run: () => softDeleteAvailability(entity._id)(),
      }),
  },
  list: {
    operation: subscribeScopedAvailabilitiesPaginated,
    selectionSet: {
      _id: 1,
      fieldEngineerId: 1,
      date: 1,
      startTime: 1,
      endTime: 1,
      useDefaultLocation: 1,
      location: {
        city: 1,
      },
    },

    columns: [
      {
        fieldName: 'fieldEngineerId',
        columnName: 'Field Engineer',
        isAlwaysVisible: true,
        selectionSet: { fieldEngineerId: 1 },
        getFieldMapping: av => FieldEngineerName({ fieldEngineerId: av.fieldEngineerId ?? undefined }),
      },
      {
        fieldName: 'date',
        columnName: 'Date',
        isAlwaysVisible: true,
        selectionSet: { date: 1 },
        getFieldMapping: av => html`
          <span class="text-sm font-medium"> ${av.date ? formatDate(av.date) : '-'} </span>
        `,
      },
      {
        fieldName: 'startTime',
        columnName: 'Timeframe',
        isAlwaysVisible: true,
        selectionSet: { startTime: 1, endTime: 1 },
        getFieldMapping: av =>
          html` <span class="text-sm">${formatClockTime(av.startTime) || '?'} - ${formatClockTime(av.endTime) || '?'}</span> `,
      },
      {
        fieldName: 'location.city',
        columnName: 'Location',
        selectionSet: { location: { city: 1 }, useDefaultLocation: 1 },
        getFieldMapping: av => html`
          <span class="text-sm">
            ${av.useDefaultLocation ? html`<span class="text-sm">Default</span>` : (av.location?.city ?? '-')}
          </span>
        `,
      },
    ],
    searchFields: [],
    listViews: [countingTableView(filter => countScopedAvailabilities(filter)())],
  },
  onCreate: async () => {
    const newId = await showCreateAvailabilityDialog();
    return newId ?? '';
  },
};
